import { Component } from "@theme/component";

if (!customElements.get("custom-calendar")) {
  /**
   * CustomCalendar Component
   * A reusable, accessible date picker component.
   *
   * @example
   * <custom-calendar>
   *   <input type="date" data-component="date-input" hidden>
   *   <div data-component="phantom-input">Select Date</div>
   *   <div data-component="calendar"></div>
   * </custom-calendar>
   */
  class CustomCalendar extends Component {

    /** @type {HTMLInputElement | null} */
    #dateInput = null;

    /** @type {HTMLElement | null} */
    #phantomInput = null;

    /** @type {HTMLElement | null} */
    #calendarContainer = null;

    /** @type {Date} Tracks the currently displayed month/year in the calendar UI */
    #currentDate = new Date();

    /** @type {Date | null} Tracks the user's selected date */
    #selectedDate = null;


    /** @type {MutationObserver | null} */
    #observer = null;

    #months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    #days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    /** @type {{ [key: string]: string }} */
    #monthAbbreviations = {
      Jan: "01", Feb: "02", Mar: "03", Apr: "04", May: "05", Jun: "06",
      Jul: "07", Aug: "08", Sep: "09", Oct: "10", Nov: "11", Dec: "12",
    };

    /**
     * Parses an ordinal-formatted date string ("29th Apr 2026") back into YYYY-MM-DD.
     * Returns the original string if it's already in ISO format or can't be parsed.
     * @param {string} dateStr - Date in ordinal format or YYYY-MM-DD
     * @returns {string} Date in YYYY-MM-DD format
     */
    #parseOrdinalDate = (dateStr) => {
      if (!dateStr) return "";
      const trimmed = dateStr.trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

      const match = trimmed.match(/^(\d{1,2})(?:st|nd|rd|th)\s+(\w{3})\s+(\d{4})$/);
      if (!match) return dateStr;

      const [, day, monthStr, year] = match;
      const month = this.#monthAbbreviations[monthStr];
      if (!month) return dateStr;

      return `${year}-${month}-${day.padStart(2, "0")}`;
    };

    /**
     * Initializes the component, selects required elements, and sets up state.
     */
    #init = () => {
      this.#observer?.disconnect();
      const host = /** @type {HTMLElement} */ (this);

      this.#dateInput = this.querySelector('[data-component="date-input"]');
      this.#phantomInput = this.querySelector(
        '[data-component="phantom-input"]',
      );
      this.#calendarContainer = this.querySelector(
        '[data-component="calendar"]',
      );

      // Error handling: Check for required child elements
      if (!this.#dateInput) {
        console.error(
          "CustomCalendar: Missing required child element with [data-component='date-input'].",
        );
        return;
      }
      if (!this.#calendarContainer) {
        console.error(
          "CustomCalendar: Missing required child element with [data-component='calendar'].",
        );
        return;
      }

      // Restore the date from the server-rendered data-arrival-value attribute.
      // The cart attribute stores an ordinal format ("29th Apr 2026") which
      // <input type="date"> rejects — so we parse it to YYYY-MM-DD.
      if (this.#dateInput && !this.#dateInput.value) {
        const arrivalValue = this.getAttribute("data-arrival-value") || "";
        if (arrivalValue) {
          const isoDate = this.#parseOrdinalDate(arrivalValue);
          if (isoDate) {
            this.#dateInput.value = isoDate;
          }
        }
      }

      if (this.#dateInput && this.#dateInput.value) {
        this.#selectedDate = new Date(this.#dateInput.value + "T00:00:00");
        this.#currentDate = new Date(this.#dateInput.value + "T00:00:00");
      }

      const hostMin = this.getAttribute("min") || (this.#dateInput && this.#dateInput.min);
      if ((!this.#selectedDate && hostMin) || (this.#dateInput && !this.#dateInput.value && hostMin)) {
        const parsedMin = new Date(hostMin + "T00:00:00");
        if (!isNaN(parsedMin.getTime())) {
          this.#currentDate = parsedMin;
        }
      }

      // Re-render if the container was emptied (e.g., by morphing)
      if (this.#calendarContainer.innerHTML.trim() === "") {
        this.#render();
      }

      this.#updatePhantomInput();
      this.#observer?.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ["max", "min"] });
    };

    /**
     * Handles closing this calendar if another one opens.
     * @param {CustomEvent} e
     */
    #handleOtherCalendarOpen = (e) => {
      if (e.detail && e.detail.target !== this) {
        this.classList.remove("is-open");
      }
    };

    /**
     * Handles closing the calendar when clicking outside.
     * @param {MouseEvent} e
     */
    #handleDocumentClick = (e) => {
      const target = /** @type {Node} */ (e.target);
      if (!this.contains(target)) {
        this.classList.remove("is-open");
      }
    };

    /**
     * Handles component-level clicks via delegation.
     * @param {MouseEvent} e
     */
    #handleComponentClick = (e) => {
      const target = /** @type {HTMLElement} */ (e.target);

      // 1. Handle Phantom Input (Toggle Calendar)
      const phantomInput = target.closest('[data-component="phantom-input"]');
      if (phantomInput && this.contains(phantomInput)) {
        e.stopPropagation();
        const wasOpen = this.classList.contains("is-open");

        if (!wasOpen) {
          // Re-render if the container became empty (e.g. after a morph)
          if (
            this.#calendarContainer &&
            this.#calendarContainer.innerHTML.trim() === ""
          ) {
            this.#render();
          }

          // Close other calendars before opening this one
          document.dispatchEvent(
            new CustomEvent("custom-calendar:open", {
              detail: { target: this },
            }),
          );
          this.classList.add("is-open");
        } else {
          this.classList.remove("is-open");
        }
        return;
      }

      // 2. Handle Navigation Buttons
      const navBtn = /** @type {HTMLElement | null} */ (
        target.closest(".custom-calendar__nav-btn")
      );
      if (navBtn && this.contains(navBtn)) {
        e.stopPropagation();
        const action = navBtn.dataset.action;
        if (action === "prev") {
          this.#currentDate.setMonth(this.#currentDate.getMonth() - 1);
        } else if (action === "next") {
          this.#currentDate.setMonth(this.#currentDate.getMonth() + 1);
        }
        this.#render();
        return;
      }

      // 3. Handle Day Click
      const dayBtn = /** @type {HTMLElement | null} */ (
        target.closest(".custom-calendar__day:not(.is-disabled)")
      );
      if (dayBtn && this.contains(dayBtn)) {
        e.stopPropagation();
        const dateStr = dayBtn.dataset.date;
        if (!dateStr) return;

        this.#selectedDate = new Date(dateStr + "T00:00:00");

        if (this.#dateInput) {
          this.#dateInput.value = dateStr;
          this.#dateInput.dispatchEvent(new Event("change", { bubbles: true }));
        }

        this.#updatePhantomInput();
        this.classList.remove("is-open");
        this.#render();
        return;
      }


      // Prevent clicks inside the component from reaching document (closing the calendar)
      if (this.classList.contains("is-open")) {
        e.stopPropagation();
      }
    };

    /**
     * Renders the calendar header, weekdays, and the days grid.
     */
    #render = () => {
      if (!this.#calendarContainer) return;

      this.#observer?.disconnect();

      const year = this.#currentDate.getFullYear();
      const month = this.#currentDate.getMonth();

      // Determine day counts and starting points
      const firstDayOfMonth = new Date(year, month, 1).getDay();
      const numDaysInMonth = new Date(year, month + 1, 0).getDate();
      const numDaysInPrevMonth = new Date(year, month, 0).getDate();

      const currentYearActual = new Date().getFullYear();
      const parseYearFromIso = (iso) => {
        if (!iso) return NaN;
        const y = parseInt(iso.split("-")[0], 10);
        return isNaN(y) ? NaN : y;
      };
      const hostMinAttr = this.getAttribute("min") || (this.#dateInput && this.#dateInput.min);
      const hostMaxAttr = this.getAttribute("max") || (this.#dateInput && this.#dateInput.max);
      const minYearParsed = parseYearFromIso(hostMinAttr);
      const maxYearParsed = parseYearFromIso(hostMaxAttr);
      const minYear = Math.min(!isNaN(minYearParsed) ? minYearParsed : currentYearActual, year);
      const maxYear = Math.max(!isNaN(maxYearParsed) ? maxYearParsed : currentYearActual + 2, year);

      const monthOptions = this.#months
        .map((name, i) => `<option value="${i}"${i === month ? " selected" : ""}>${name}</option>`)
        .join("");
      let yearOptions = "";
      for (let y = minYear; y <= maxYear; y++) {
        yearOptions += `<option value="${y}"${y === year ? " selected" : ""}>${y}</option>`;
      }

      let html = `
        <div class="custom-calendar__header">
          <button type="button" class="custom-calendar__nav-btn" data-action="prev" aria-label="Previous month">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
          <div class="custom-calendar__title">
            <select class="custom-calendar__month-select" data-component="month-select" aria-label="Month">${monthOptions}</select>
            <select class="custom-calendar__year-select" data-component="year-select" aria-label="Year">${yearOptions}</select>
          </div>
          <button type="button" class="custom-calendar__nav-btn" data-action="next" aria-label="Next month">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        </div>
        <div class="custom-calendar__body">
          <div class="custom-calendar__weekdays">
            ${this.#days.map((day) => `<div class="custom-calendar__weekday">${day}</div>`).join("")}
          </div>
          <div class="custom-calendar__days">
      `;

      // Sunday-first week: firstDayOfMonth already gives Sunday=0..Saturday=6
      let startDayAdjusted = firstDayOfMonth;

      const prevMonthDate = new Date(year, month - 1, 1);
      const nextMonthDate = new Date(year, month + 1, 1);

      let minDate = new Date();
      minDate.setHours(0, 0, 0, 0);
      const hostMin = this.getAttribute("min") || (this.#dateInput && this.#dateInput.min);
      if (hostMin) {
        const parsedMin = new Date(hostMin + "T00:00:00");
        if (!isNaN(parsedMin.getTime())) {
          minDate = parsedMin;
        }
      }

      let maxDate = null;
      const hostMax = this.getAttribute("max") || (this.#dateInput && this.#dateInput.max);
      if (hostMax) {
        maxDate = new Date(hostMax + "T00:00:00");
      }

      const isDateDisabled = (checkYear, checkMonth, checkDay) => {
        const currentDateBeingRendered = new Date(checkYear, checkMonth, checkDay);
        const isPast = currentDateBeingRendered < minDate;

        let isBeyondMax = false;
        if (maxDate) {
          const currentDateBeingRendered = new Date(checkYear, checkMonth, checkDay);
          isBeyondMax = currentDateBeingRendered > maxDate;
        }
        
        return isPast || isBeyondMax;
      };

      // Render days from the previous month
      for (let i = startDayAdjusted - 1; i >= 0; i--) {
        const dayNum = numDaysInPrevMonth - i;
        const prevYear = prevMonthDate.getFullYear();
        const prevMonth = prevMonthDate.getMonth();
        const dateStr = `${prevYear}-${String(prevMonth + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
        let classes = ["custom-calendar__day", "is-prev-month"];
        
        if (isDateDisabled(prevYear, prevMonth, dayNum)) {
          classes.push("is-disabled");
          html += `<button type="button" class="${classes.join(" ")}" disabled>${dayNum}</button>`;
        } else {
          html += `<button type="button" class="${classes.join(" ")}" data-date="${dateStr}">${dayNum}</button>`;
        }
      }

      // Render days of the current month
      for (let i = 1; i <= numDaysInMonth; i++) {
        const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(i).padStart(2, "0")}`;
        let classes = ["custom-calendar__day"];

        // Highlight logic
        if (this.#selectedDate) {
          if (
            this.#selectedDate.getFullYear() === year &&
            this.#selectedDate.getMonth() === month &&
            this.#selectedDate.getDate() === i
          ) {
            classes.push("is-selected");
          }
        }

        if (isDateDisabled(year, month, i)) {
          classes.push("is-disabled");
          html += `<button type="button" class="${classes.join(" ")}" disabled>${i}</button>`;
        } else {
          html += `<button type="button" class="${classes.join(" ")}" data-date="${dateStr}">${i}</button>`;
        }
      }

      // Render days from the next month to fill out a 6x7 grid (42 cells total)
      const totalCells = startDayAdjusted + numDaysInMonth;
      const nextMonthDays = 42 - totalCells;

      for (let i = 1; i <= nextMonthDays; i++) {
        const nextYear = nextMonthDate.getFullYear();
        const nextMonth = nextMonthDate.getMonth();
        const dateStr = `${nextYear}-${String(nextMonth + 1).padStart(2, "0")}-${String(i).padStart(2, "0")}`;
        let classes = ["custom-calendar__day", "is-next-month"];
        
        if (isDateDisabled(nextYear, nextMonth, i)) {
          classes.push("is-disabled");
          html += `<button type="button" class="${classes.join(" ")}" disabled>${i}</button>`;
        } else {
          html += `<button type="button" class="${classes.join(" ")}" data-date="${dateStr}">${i}</button>`;
        }
      }

      html += `
          </div>
        </div>
      `;

      this.#calendarContainer.innerHTML = html;
      this.#observer?.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ["max", "min"] });
    };

    /**
     * Handles month navigation.
     * @param {MouseEvent} e
     */
    #handleNavClick = (e) => {
      const target = /** @type {HTMLElement} */ (e.currentTarget);
      const action = target.dataset.action;
      if (action === "prev") {
        this.#currentDate.setMonth(this.#currentDate.getMonth() - 1);
      } else if (action === "next") {
        this.#currentDate.setMonth(this.#currentDate.getMonth() + 1);
      }
      this.#render();
    };


    /**
     * Updates the visible "phantom" input with the newly formatted date string or placeholder.
     */
    #updatePhantomInput = () => {
      const phantomInput = this.querySelector(
        '[data-component="phantom-input"]',
      );
      if (!phantomInput) return;

      this.#observer?.disconnect();

      const host = /** @type {HTMLElement} */ (this);
      let displayText = "Arrival in Canada";

      if (this.#selectedDate) {
        displayText = `${String(this.#selectedDate.getDate()).padStart(2, "0")}/${String(this.#selectedDate.getMonth() + 1).padStart(2, "0")}/${this.#selectedDate.getFullYear()}`;
        this.classList.add("selected");
      } else {
        const placeholderAttr =
          host.getAttribute("data-placeholder") ||
          host.dataset.placeholder ||
          "";
        if (placeholderAttr.trim()) {
          displayText = placeholderAttr.trim();
        }
        this.classList.remove("selected");
      }

      let existingSpan = phantomInput.querySelector(".selected-date-text");

      if (!existingSpan) {
        existingSpan = document.createElement("span");
        existingSpan.className = "selected-date-text";
        phantomInput.appendChild(existingSpan);
      }

      existingSpan.textContent = displayText;
      this.#observer?.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ["max", "min"] });
    };

    /**
     * Handles selecting a new date from the calendar grid.
     * @param {MouseEvent} e
     */
    #handleDayClick = (e) => {
      e.stopPropagation();
      const target = /** @type {HTMLElement} */ (e.currentTarget);
      const dateStr = target.dataset.date;
      if (!dateStr) return;


      this.#selectedDate = new Date(dateStr + "T00:00:00");

      // Update real hidden input and trigger change event
      if (this.#dateInput) {
        this.#dateInput.value = dateStr;
        this.#dateInput.dispatchEvent(new Event("change", { bubbles: true }));
      }

      this.#updatePhantomInput();

      // Close the calendar popup after selection
      this.classList.remove("is-open");
      this.#render();
    };

    /**
     * Handles month/year dropdown changes from the calendar header.
     * @param {Event} e
     */
    #handleSelectChange = (e) => {
      const target = /** @type {HTMLElement | null} */ (e.target);
      if (!target || !target.matches) return;
      if (target.matches('[data-component="month-select"]')) {
        const newMonth = parseInt(/** @type {HTMLSelectElement} */ (target).value, 10);
        if (!isNaN(newMonth)) {
          this.#currentDate = new Date(this.#currentDate.getFullYear(), newMonth, 1);
          this.#render();
        }
      } else if (target.matches('[data-component="year-select"]')) {
        const newYear = parseInt(/** @type {HTMLSelectElement} */ (target).value, 10);
        if (!isNaN(newYear)) {
          this.#currentDate = new Date(newYear, this.#currentDate.getMonth(), 1);
          this.#render();
        }
      }
    };

    connectedCallback() {
      super.connectedCallback();
      this.#init();

      // Use event delegation for the component's interactive parts
      this.addEventListener("click", this.#handleComponentClick);
      this.addEventListener("change", this.#handleSelectChange);

      // Close the calendar if the user clicks outside of it
      document.addEventListener("click", this.#handleDocumentClick);

      // Listen for other calendars opening
      document.addEventListener(
        "custom-calendar:open",
        this.#handleOtherCalendarOpen,
      );

      // Setup MutationObserver to handle DOM changes and attribute mutations
      if (!this.#observer) {
        this.#observer = new MutationObserver((mutations) => {
          let shouldForceRender = false;
          for (const m of mutations) {
            if (m.type === 'attributes' && (m.attributeName === 'max' || m.attributeName === 'min')) {
              shouldForceRender = true;
              break;
            }
          }
          this.#init();
          if (shouldForceRender) {
            this.#render();
          }
        });
        this.#observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ["max", "min"] });
      }
    }

    updatedCallback() {
      super.updatedCallback();
      this.#init();
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      if (this.#observer) {
        this.#observer.disconnect();
      }
      document.removeEventListener("click", this.#handleDocumentClick);
      document.removeEventListener(
        "custom-calendar:open",
        this.#handleOtherCalendarOpen,
      );
    }
  }

  customElements.define("custom-calendar", CustomCalendar);
}
